// SCR-STAFF-001 — انتخاب مستأجر و شعبه (US-IAM-002، AC-IAM-001.2، AC-IAM-001.3، AC-IAM-002.1، AC-IAM-002.2).
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ErrorCodes, type MyTenant } from '@phoenix/api-client';
import { errorMessageKey, useTranslation } from '@phoenix/i18n';
import { ErrorState, LoadingState } from '@phoenix/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from './AuthProvider';
import { useTenantSession } from './TenantSessionProvider';

export const SCR_STAFF_001 = 'SCR-STAFF-001';

/** نام مرکز (Name ممکن است تا AP-PLT-01 خالی باشد). */
export function useTenantName() {
  const { t } = useTranslation('shell');
  return (tenant: Pick<MyTenant, 'name'> | null | undefined) => tenant?.name || t('shell:tenant.unnamed');
}

function AccessModeHint({ mode }: { mode: string }) {
  const { t } = useTranslation('auth');
  if (mode === 'Full') {
    return null;
  }
  return (
    <Typography variant="caption" color="warning.main" component="span" sx={{ ms: 1 }}>
      {t(mode === 'Locked' ? 'auth:tenant_select.mode_locked' : 'auth:tenant_select.mode_read_only')}
    </Typography>
  );
}

/** کارت مرکز: لوگو (یا حرف نخست نام)، نام، نقش مالک/عضو، حالت دسترسی و دلیل غیرقابل انتخاب بودن (AC-IAM-001.3). */
function TenantCardLabel({ tenant }: { tenant: MyTenant }) {
  const { t } = useTranslation('auth');
  const tenantName = useTenantName();
  const name = tenantName(tenant);
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, minWidth: 0 }}>
      <Avatar
        src={tenant.logoUrl ?? undefined}
        alt=""
        variant="rounded"
        sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}
      >
        {name.charAt(0)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography component="span" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            data-testid={`tenant-role-${tenant.tenantId}`}
            label={t(tenant.isOwner ? 'auth:tenant_select.role_owner' : 'auth:tenant_select.role_member')}
          />
          <AccessModeHint mode={tenant.accessMode} />
        </Stack>
        {!tenant.selectable && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="span"
            sx={{ display: 'block' }}
            data-testid={`tenant-unavailable-${tenant.tenantId}`}
          >
            {t('auth:tenant_select.locked_not_owner')}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export function TenantSelectPage() {
  const { t } = useTranslation('auth');
  const auth = useAuth();
  const session = useTenantSession();
  const { tenants, status, selectError, loadError } = session;
  const [tenantId, setTenantId] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [touched, setTouched] = useState(false);

  // نخستین مرکز قابل انتخاب (مستأجر Locked برای غیرمالک غیرفعال است — C-AGENT-182).
  useEffect(() => {
    const first = tenants.find((tenant) => tenant.selectable) ?? tenants[0];
    if (!tenantId && first) {
      setTenantId(first.tenantId);
    }
  }, [tenants, tenantId]);
  const noneSelectable = tenants.length > 0 && tenants.every((tenant) => !tenant.selectable);

  const selected = tenants.find((tenant) => tenant.tenantId === tenantId);
  const needsBranch = (selected?.branches.length ?? 0) > 1;

  useEffect(() => {
    setBranchId(selected && selected.branches.length === 1 ? (selected.branches[0]?.branchId ?? '') : '');
  }, [selected]);

  if (status === 'active') {
    return <Navigate to="/" replace />;
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!selected || !selected.selectable || (needsBranch && !branchId)) {
      return;
    }
    void session.selectTenant(selected.tenantId, branchId || null);
  };

  let body;
  if (status === 'loading' || status === 'selecting') {
    body = <LoadingState />;
  } else if (status === 'error') {
    body = <ErrorState error={loadError} onRetry={session.reload} />;
  } else if (status === 'no-membership') {
    // AC-IAM-001.2: «عضویت فعالی ندارید» (PHX-IAM-0103) و ورود به هیچ مستأجری.
    body = (
      <Alert severity="warning" data-testid="no-membership" data-error-code={ErrorCodes.noActiveMembership}>
        <AlertTitle>{t(errorMessageKey(ErrorCodes.noActiveMembership))}</AlertTitle>
        {t('auth:tenant_select.no_membership_hint')}
      </Alert>
    );
  } else {
    body = (
      <Box component="form" onSubmit={submit} noValidate>
        {selectError && <ErrorState error={selectError} compact />}
        {noneSelectable && (
          <Alert severity="warning" sx={{ mb: 1 }} data-testid="no-selectable-tenant">
            {t('auth:tenant_select.none_selectable')}
          </Alert>
        )}
        {selectError && tenants.length > 1 && (
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {t('auth:tenant_select.choose_other')}
          </Typography>
        )}
        <RadioGroup
          aria-label={t('auth:tenant_select.tenant_label')}
          value={tenantId}
          onChange={(event) => setTenantId(event.target.value)}
        >
          {tenants.map((tenant) => (
            <Paper
              key={tenant.tenantId}
              variant="outlined"
              sx={{
                px: 1.5,
                mb: 1,
                borderColor: tenant.tenantId === tenantId ? 'primary.main' : undefined,
                opacity: tenant.selectable ? 1 : 0.7,
              }}
            >
              <FormControlLabel
                value={tenant.tenantId}
                control={<Radio />}
                disabled={!tenant.selectable}
                data-testid={`tenant-option-${tenant.tenantId}`}
                data-selectable={tenant.selectable}
                sx={{ width: '100%', m: 0 }}
                label={<TenantCardLabel tenant={tenant} />}
              />
            </Paper>
          ))}
        </RadioGroup>
        {needsBranch && selected && (
          <TextField
            select
            required
            sx={{ mt: 2 }}
            label={t('auth:tenant_select.branch_label')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            error={touched && !branchId}
            helperText={
              touched && !branchId ? t('common:validation.required') : t('auth:tenant_select.branch_hint')
            }
            slotProps={{ htmlInput: { 'data-testid': 'branch-select' } }}
          >
            {selected.branches.map((branch) => (
              <MenuItem key={branch.branchId} value={branch.branchId}>
                {branch.name || t('shell:branch.unnamed')}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 3 }}
          fullWidth
          data-testid="tenant-select-continue"
          disabled={noneSelectable}
        >
          {t('auth:tenant_select.continue')}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      data-screen-id={SCR_STAFF_001}
      sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}
    >
      <Paper variant="outlined" sx={{ p: 4, width: '100%', maxWidth: 480 }} component="main">
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h1">{t('auth:tenant_select.title')}</Typography>
          <Typography color="text.secondary">{t('auth:tenant_select.description')}</Typography>
        </Stack>
        {body}
        <Button onClick={() => void auth.logout()} sx={{ mt: 2 }} fullWidth>
          {t('auth:account.logout')}
        </Button>
      </Paper>
    </Box>
  );
}
